/**
 * Translation between our TransformPath authoring format and KHR_interactivity's
 * glTF nodeIndex / JSON-pointer references.
 *
 * Two scopes today:
 *   1. KHR_node_selectability / KHR_node_hoverability events:
 *      `target: TransformPath` (ours)  ↔  `nodeIndex: int` (KHR)
 *   2. KHR pointer/get, pointer/set, and pointer/interpolate:
 *      `target: TransformPath` + `property: "translation"|"rotation"|"scale"` (ours)
 *      ↔  `pointer: "/nodes/<N>/<property>"` (KHR — a static pointer; ratified templates use `[x]` for int sockets)
 *
 * Other Trace blocks with `target` configs (setActive, setState, proximity, etc.)
 * live under the TRACE_interactivity extension namespace and round-trip
 * verbatim — they aren't authored against the KHR standard ops table.
 *
 * When no translation context is provided, all converters fall back to a
 * pass-through (used by subgraph-only round-trips that don't know a scene).
 */

import type { GraphNode, ConfigValue } from '../../scenedata/behaviors/behaviorgraphdata';

// Ops whose `target` config maps to KHR's `nodeIndex` config (key rename + value type promotion).
const NODEINDEX_EVENT_OPS = new Set<string>([
    'event/onSelect',
    'event/onHoverIn',
    'event/onHoverOut',
]);

// Ops whose `target` + `property` configs map to KHR's `pointer` config.
// pointer/interpolate's p1/p2/duration are plain value inputs — flat,
// KHR-legal, nothing else to rewrite.
const POINTER_OPS = new Set<string>([
    'pointer/get',
    'pointer/set',
    'pointer/interpolate',
]);

// Trace-namespaced ops with a `target` config. The key stays `target` (we
// don't rename to `nodeIndex` — KHR's `nodeIndex` is reserved for standard
// ops), but the value is translated to/from a glTF node index on export/import.
// Keeps the wire format internally consistent: every node reference is an
// integer, every authored path is a string.
const TRACE_TARGET_OPS = new Set<string>([
    'trace/onProximity',
    'trace/setActive',
    'trace/getActive',
    'trace/getChildCount',
    'trace/setActiveChild',
    'trace/getProperty',
    'trace/setProperty',
    'trace/setState',
    'trace/getState',
    'trace/onStateChange',
    'trace/playAnimation',
    'trace/playAnimationExternal',
    'trace/playVideo',
    'trace/playMedia',
    'trace/pauseMedia',
    'trace/stopMedia',
    'trace/seekMedia',
    'trace/setMediaVolume',
    'trace/isMediaPlaying',
    'trace/getMediaVolume',
    'trace/setBehaviorEnabled',
    'pages/next',
    'pages/prev',
    'pages/getActiveIndex',
    'pages/getPageCount',
]);

/**
 * Export-time context: lookup a glTF nodeIndex from an authored TransformPath,
 * resolved relative to the host (the SceneObject that owns the BehaviorGraph).
 *
 * Implementations build this from `pathToIndex` already maintained by the
 * exporter for parent-link resolution.
 */
export interface ExportTranslationContext {
    /**
     * Absolute FQTP of the host (e.g. "Scene/Root"), without leading slash.
     * Used to resolve `$self`, `./Child`, `../Sibling`, and bare-name paths.
     */
    hostFqtp: string;
    /** FQTP → glTF node index. Keys have no leading slash. */
    pathToIndex: ReadonlyMap<string, number>;
}

/**
 * Import-time context: lookup an authored TransformPath from a glTF nodeIndex.
 * The path is emitted relative to the host when possible, absolute otherwise.
 */
export interface ImportTranslationContext {
    /** glTF node index of the host node. */
    hostIndex: number;
    /** Total number of nodes in the glTF document. */
    nodeCount: number;
    /** Look up a node's name by index. Missing → undefined. */
    nameOf(nodeIndex: number): string | undefined;
    /** Look up a node's parent index. Returns undefined for roots. */
    parentOf(nodeIndex: number): number | undefined;
}

// ============================================================================
// Export: path → nodeIndex
// ============================================================================

/**
 * Resolve an authored TransformPath to a glTF nodeIndex using export context.
 * Returns null when the path can't be resolved (caller should leave config alone).
 */
export function pathToNodeIndex(
    path: string,
    ctx: ExportTranslationContext,
): number | null {
    if (!path) {
        // Empty target ⇒ host (matches runtime resolveTarget semantics)
        return ctx.pathToIndex.get(ctx.hostFqtp) ?? null;
    }
    if (path === '$self') {
        return ctx.pathToIndex.get(ctx.hostFqtp) ?? null;
    }
    if (path === '$root') {
        // First path component is the scene root segment by convention.
        const rootSeg = ctx.hostFqtp.split('/')[0];
        return rootSeg ? (ctx.pathToIndex.get(rootSeg) ?? null) : null;
    }
    if (path === '$parent') {
        const parts = ctx.hostFqtp.split('/');
        parts.pop();
        const parentFqtp = parts.join('/');
        return parentFqtp ? (ctx.pathToIndex.get(parentFqtp) ?? null) : null;
    }
    if (path.startsWith('/')) {
        return ctx.pathToIndex.get(path.slice(1)) ?? null;
    }
    if (path.startsWith('./')) {
        const candidate = `${ctx.hostFqtp}/${path.slice(2)}`;
        return ctx.pathToIndex.get(candidate) ?? null;
    }
    if (path.startsWith('../')) {
        const parts = ctx.hostFqtp.split('/');
        let remaining = path;
        while (remaining.startsWith('../')) {
            if (parts.length === 0) return null;
            parts.pop();
            remaining = remaining.slice(3);
        }
        const candidate = remaining ? `${parts.join('/')}/${remaining}` : parts.join('/');
        return ctx.pathToIndex.get(candidate) ?? null;
    }
    // Bare name — first try as sibling of host, then as any descendant of host's
    // scene root, then as a globally-unique name (legacy behavior). This mirrors
    // TraceScene.resolveLegacyPath enough for the export translation step.
    const hostParts = ctx.hostFqtp.split('/');
    hostParts.pop();
    const sibling = hostParts.length > 0 ? `${hostParts.join('/')}/${path}` : path;
    if (ctx.pathToIndex.has(sibling)) return ctx.pathToIndex.get(sibling)!;
    // Fallback: any path that ends with /name or equals name
    for (const [fqtp, idx] of ctx.pathToIndex) {
        if (fqtp === path || fqtp.endsWith(`/${path}`)) return idx;
    }
    return null;
}

// ============================================================================
// Import: nodeIndex → path
// ============================================================================

/** Compute the absolute FQTP of a glTF node by walking parents. */
function nodeFqtp(nodeIndex: number, ctx: ImportTranslationContext): string {
    const parts: string[] = [];
    let current: number | undefined = nodeIndex;
    const seen = new Set<number>();
    while (current !== undefined) {
        if (seen.has(current)) break; // cycle guard
        seen.add(current);
        parts.unshift(ctx.nameOf(current) ?? `Node_${current}`);
        current = ctx.parentOf(current);
    }
    return parts.join('/');
}

/**
 * Translate a glTF nodeIndex to a TransformPath, preferring a path relative to
 * the host. Falls back to absolute (leading "/") when no relative form exists.
 */
export function nodeIndexToPath(
    nodeIndex: number,
    ctx: ImportTranslationContext,
): string {
    if (nodeIndex < 0 || nodeIndex >= ctx.nodeCount) {
        return `/Node_${nodeIndex}`;
    }
    if (nodeIndex === ctx.hostIndex) return '$self';

    const hostChain = ancestorChain(ctx.hostIndex, ctx);
    const targetChain = ancestorChain(nodeIndex, ctx);

    // Descendant of host?
    if (targetChain.length > hostChain.length) {
        let isDescendant = true;
        for (let i = 0; i < hostChain.length; i++) {
            if (targetChain[i] !== hostChain[i]) {
                isDescendant = false;
                break;
            }
        }
        if (isDescendant) {
            const rel = targetChain.slice(hostChain.length).map(idx => ctx.nameOf(idx) ?? `Node_${idx}`);
            return './' + rel.join('/');
        }
    }

    // Common ancestor → ../ steps up, then down to target
    let common = 0;
    const maxLen = Math.min(hostChain.length, targetChain.length);
    for (let i = 0; i < maxLen; i++) {
        if (hostChain[i] === targetChain[i]) common = i + 1;
        else break;
    }
    if (common === 0) {
        // No shared root — emit absolute path
        return '/' + nodeFqtp(nodeIndex, ctx);
    }
    const stepsUp = hostChain.length - common;
    const upPart = '../'.repeat(stepsUp);
    const downParts = targetChain.slice(common).map(idx => ctx.nameOf(idx) ?? `Node_${idx}`);
    return upPart + downParts.join('/');
}

function ancestorChain(nodeIndex: number, ctx: ImportTranslationContext): number[] {
    const chain: number[] = [];
    let current: number | undefined = nodeIndex;
    const seen = new Set<number>();
    while (current !== undefined) {
        if (seen.has(current)) break;
        seen.add(current);
        chain.unshift(current);
        current = ctx.parentOf(current);
    }
    return chain;
}

// ============================================================================
// Node-level rewriters (called from converters)
// ============================================================================

const VALID_POINTER_PROPS = new Set(['translation', 'rotation', 'scale']);

/**
 * On export: if `op` references a scene node in its configuration, rewrite to
 * the spec-mandated wire form; otherwise return the input.
 *
 * - event/onSelect|onHoverIn|onHoverOut: `target: path` → `nodeIndex: int`
 * - pointer/get|set|interpolate: `target: path` + `property: name` → `pointer: "/nodes/N/name"`
 * - Trace ops with a `target` config: `target: path` → `target: int` (key stays,
 *   value promoted to integer for round-trip determinism).
 */
export function rewriteConfigForKHRExport(
    op: string,
    configuration: Record<string, ConfigValue> | undefined,
    ctx: ExportTranslationContext,
): Record<string, ConfigValue> | undefined {
    if (!configuration) return configuration;

    if (NODEINDEX_EVENT_OPS.has(op)) {
        const targetCv = configuration.target;
        // Already in wire form: a config the IMPORTER could not translate
        // (issue #852) keeps its `nodeIndex` and has no `target` at all. The
        // empty-target ⇒ host rule below is for an authored EMPTY target, not
        // for an absent key — applying it here would retarget the node at the
        // host and silently lose what the document said.
        if (!targetCv && configuration.nodeIndex !== undefined) return configuration;
        const targetPath = targetCv?.value?.[0];
        const idx = pathToNodeIndex(typeof targetPath === 'string' ? targetPath : '', ctx);
        if (idx === null) return stripTarget(configuration);
        return {
            ...stripTarget(configuration),
            nodeIndex: { type: 0, value: [idx] },
        };
    }

    if (POINTER_OPS.has(op)) {
        // Already in wire form — see the NODEINDEX_EVENT_OPS note above. A
        // pointer the importer left alone (`/nodes/[nodeIndex]/translation`,
        // whose index is a VALUE rather than a config) is written back
        // verbatim; deriving one from an absent target would point it at the
        // host instead.
        if (!configuration.target && configuration.pointer !== undefined) return configuration;
        const targetRaw = configuration.target?.value?.[0];
        const propRaw = configuration.property?.value?.[0];
        const targetPath = typeof targetRaw === 'string' ? targetRaw : '';
        const property = typeof propRaw === 'string' ? propRaw : 'translation';
        const idx = pathToNodeIndex(targetPath, ctx);
        if (idx === null || !VALID_POINTER_PROPS.has(property)) {
            // Leave the config alone if we can't form a valid pointer.
            return configuration;
        }
        const next: Record<string, ConfigValue> = { ...configuration };
        delete next.target;
        delete next.property;
        next.pointer = { type: 0, value: [`/nodes/${idx}/${property}`] };
        return next;
    }

    if (TRACE_TARGET_OPS.has(op)) {
        const targetRaw = configuration.target?.value?.[0];
        if (typeof targetRaw !== 'string') return configuration;
        const idx = pathToNodeIndex(targetRaw, ctx);
        if (idx === null) return configuration;
        return {
            ...configuration,
            target: { type: 0, value: [idx] },
        };
    }

    return configuration;
}

/**
 * On import: reverse of the above.
 */
export function rewriteConfigForKHRImport(
    op: string,
    configuration: Record<string, ConfigValue> | undefined,
    ctx: ImportTranslationContext,
): Record<string, ConfigValue> | undefined {
    if (!configuration) return configuration;

    if (NODEINDEX_EVENT_OPS.has(op)) {
        const nodeIndexCv = configuration.nodeIndex;
        const raw = nodeIndexCv?.value?.[0];
        const idx = typeof raw === 'number' ? raw : Number(raw);
        if (!Number.isInteger(idx)) return configuration;
        const path = nodeIndexToPath(idx, ctx);
        const next: Record<string, ConfigValue> = { ...configuration };
        delete next.nodeIndex;
        next.target = { type: 0, value: [path] };
        return next;
    }

    if (POINTER_OPS.has(op)) {
        const pointerCv = configuration.pointer;
        const raw = pointerCv?.value?.[0];
        if (typeof raw !== 'string') return configuration;
        const parsed = parseNodePointer(raw);
        if (!parsed) return configuration;
        const next: Record<string, ConfigValue> = { ...configuration };
        delete next.pointer;
        next.target = { type: 0, value: [nodeIndexToPath(parsed.nodeIndex, ctx)] };
        next.property = { type: 0, value: [parsed.property] };
        return next;
    }

    if (TRACE_TARGET_OPS.has(op)) {
        const targetRaw = configuration.target?.value?.[0];
        // Number → translate back to path. String → leave alone (back-compat
        // with pre-translation exports and third-party authors).
        if (typeof targetRaw !== 'number') return configuration;
        if (!Number.isInteger(targetRaw)) return configuration;
        return {
            ...configuration,
            target: { type: 0, value: [nodeIndexToPath(targetRaw, ctx)] },
        };
    }

    return configuration;
}

function stripTarget(configuration: Record<string, ConfigValue>): Record<string, ConfigValue> {
    const next: Record<string, ConfigValue> = { ...configuration };
    delete next.target;
    return next;
}

function parseNodePointer(pointer: string): { nodeIndex: number; property: string } | null {
    // Only the static `/nodes/<N>/<property>` form; templates (`[x]`) stay verbatim.
    const match = pointer.match(/^\/nodes\/(\d+)\/([^/]+)$/);
    if (!match) return null;
    const idx = Number.parseInt(match[1], 10);
    if (!Number.isInteger(idx)) return null;
    return { nodeIndex: idx, property: match[2] };
}

// ============================================================================
// Helper for callers that walk a GraphData
// ============================================================================

/**
 * Apply an export rewrite to every node in-place on a config record (returned).
 * Convenience used by GraphDataToKHR.
 */
export function rewriteNodeConfigForKHRExport(
    node: GraphNode,
    declOps: readonly string[],
    ctx: ExportTranslationContext,
): Record<string, ConfigValue> | undefined {
    const op = declOps[node.declaration];
    if (!op) return node.configuration;
    return rewriteConfigForKHRExport(op, node.configuration, ctx);
}

/** Apply an import rewrite to a config record. */
export function rewriteNodeConfigForKHRImport(
    node: GraphNode,
    declOps: readonly string[],
    ctx: ImportTranslationContext,
): Record<string, ConfigValue> | undefined {
    const op = declOps[node.declaration];
    if (!op) return node.configuration;
    return rewriteConfigForKHRImport(op, node.configuration, ctx);
}
